// Percorso file: src/utils/importTransactions.js
//
// IMPORT DEI MOVIMENTI DI CASSA DA UN FOGLIO EXCEL — la lettura e il vaglio
// delle righe, riga per riga, PRIMA che qualcosa venga scritto.
//
// ⚠️ MODULO PURO: nessun React, nessun hook, nessuna chiamata al backend e
// nessuna dipendenza da `xlsx`. Riceve le righe già estratte dal foglio
// (`XLSX.utils.sheet_to_json(sheet, { defval: "" })`, lo stesso pattern di
// EventModal.jsx) e le due liste che il backend già dà (GET /contacts e
// GET /categories); restituisce cosa entrerà e cosa è scartato, col motivo.
// Essere puro è ciò che permette di provarlo a tavolino, in Node, senza
// avviare nulla.
//
// COSA PRODUCE UNA RIGA VALIDA: un movimento nella STESSA FORMA che produce
// il form (TransactionForm.jsx, `handleSubmit`): { date "AAAA-MM-GG",
// clientOrSupplier <oggetto contatto intero>, description, category <oggetto
// categoria intero>, bank1, bank2, cash }. Nessun `id` (lo conia il server),
// nessun `isInitialBalance` (l'import non tocca il saldo iniziale).
//
// LE REGOLE DEL VAGLIO (decisioni del committente, cantiere FINANCE-IMPORT):
//   · colonne riconosciute per NOME di intestazione, non per posizione:
//     Date, Client, Description, Category, BOQ, CBA, CASH (trim, senza
//     distinguere maiuscole); senza Date, Client, Category e almeno una
//     colonna di importo il file si rifiuta per intero;
//   · la data del foglio è GG-MM-AAAA (o una data Excel) e diventa
//     AAAA-MM-GG, il formato di sistema;
//   · Client e Category si risolvono PER NOME fra ciò che la PWA ha già:
//     nome sconosciuto → riga scartata. L'import non crea mai né clienti
//     né categorie;
//   · BOQ → bank1, CBA → bank2, CASH → cash; vuoto → 0; ESATTAMENTE UNA
//     delle tre deve essere diversa da zero;
//   · la riga del saldo iniziale (INITIAL BALANCE) è IGNORATA di proposito:
//     non è un errore, è la decisione che l'import aggiunge solo movimenti;
//   · nessuna difesa dai doppioni: l'import non confronta con l'esistente.

// Le intestazioni attese e la chiave interna con cui si ragiona.
const COLONNE = {
  date: "date",
  client: "client",
  description: "description",
  category: "category",
  boq: "boq",
  cba: "cba",
  cash: "cash",
};

// Da BOQ/CBA/CASH (a schermo e nel file) a bank1/bank2/cash (nel modello):
// la stessa mappa di `accounts` in ClubFinance.jsx.
const CONTI = [
  { colonna: "boq", chiave: "bank1", label: "BOQ" },
  { colonna: "cba", chiave: "bank2", label: "CBA" },
  { colonna: "cash", chiave: "cash", label: "CASH" },
];

const normalizzaTesto = (v) => String(v == null ? "" : v).trim();
const chiaveDiConfronto = (v) => normalizzaTesto(v).toLowerCase();

/**
 * Il nome con cui un contatto si mostra: la stessa regola della tendina del
 * form (TransactionForm.jsx: `isCompany ? companyName : firstName lastName`).
 */
export function nomeDelContatto(c) {
  if (!c || typeof c !== "object") return "";
  if (c.isCompany) return normalizzaTesto(c.companyName);
  return `${normalizzaTesto(c.firstName)} ${normalizzaTesto(c.lastName)}`.trim();
}

/**
 * I nomi con cui un contatto può comparire in un foglio: quello del form
 * (sopra) e quello dell'export di Finance (`companyName || firstName
 * lastName`, ClubFinance.jsx `buildExportRows`). Di norma coincidono; se
 * un contatto ha `companyName` ma non è marcato azienda, l'export scrive
 * la ragione sociale e il form il nome — si accettano entrambi.
 */
function nomiDelContatto(c) {
  const nomi = new Set();
  const delForm = chiaveDiConfronto(nomeDelContatto(c));
  if (delForm) nomi.add(delForm);
  const dellExport = chiaveDiConfronto(
    c.companyName ||
      `${normalizzaTesto(c.firstName)} ${normalizzaTesto(c.lastName)}`.trim()
  );
  if (dellExport) nomi.add(dellExport);
  return nomi;
}

/**
 * Riconosce le colonne del foglio per nome di intestazione (trim, senza
 * distinguere maiuscole). Restituisce la mappa chiave interna → intestazione
 * originale, com'è scritta nel file, per poter rileggere ogni riga.
 */
export function riconosciColonne(intestazioni) {
  const mappa = {};
  for (const originale of intestazioni) {
    const nome = chiaveDiConfronto(originale);
    if (COLONNE[nome] && !(nome in mappa)) mappa[nome] = originale;
  }
  return mappa;
}

/**
 * Le intestazioni essenziali che mancano: Date, Client, Category e almeno
 * una fra BOQ/CBA/CASH. Lista vuota = si può procedere.
 */
export function colonneMancanti(mappa) {
  const mancano = [];
  if (!mappa.date) mancano.push("Date");
  if (!mappa.client) mancano.push("Client");
  if (!mappa.category) mancano.push("Category");
  if (!mappa.boq && !mappa.cba && !mappa.cash) mancano.push("BOQ/CBA/CASH");
  return mancano;
}

const dueCifre = (n) => String(n).padStart(2, "0");

/**
 * La data della riga in AAAA-MM-GG, o "" se non interpretabile.
 *
 * Tre forme ammesse:
 *   · stringa GG-MM-AAAA (il formato dell'export di Finance; si accetta anche
 *     il separatore "/");
 *   · un oggetto Date (SheetJS lo produce se il foglio è letto con
 *     `cellDates`);
 *   · un numero: il seriale di Excel (giorni dal 30/12/1899), che è come
 *     SheetJS restituisce una cella-data senza `cellDates`.
 * Una stringa AAAA-MM-GG si accetta tale e quale. Il giorno e il mese si
 * verificano davvero (31-02-2026 non è una data).
 */
export function dataInFormatoDiSistema(v) {
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return "";
    return `${v.getFullYear()}-${dueCifre(v.getMonth() + 1)}-${dueCifre(v.getDate())}`;
  }
  if (typeof v === "number") {
    if (!isFinite(v) || v < 1) return "";
    // 25569 è il seriale del 1/1/1970; il resto è aritmetica in UTC, così il
    // fuso del browser non sposta la data di un giorno.
    const d = new Date(Math.round((v - 25569) * 86400000));
    return d.toISOString().slice(0, 10);
  }
  const s = normalizzaTesto(v);
  if (!s) return "";
  let anno;
  let mese;
  let giorno;
  let m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
  if (m) {
    giorno = Number(m[1]);
    mese = Number(m[2]);
    anno = Number(m[3]);
  } else {
    m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return "";
    anno = Number(m[1]);
    mese = Number(m[2]);
    giorno = Number(m[3]);
  }
  const prova = new Date(Date.UTC(anno, mese - 1, giorno));
  if (
    prova.getUTCFullYear() !== anno ||
    prova.getUTCMonth() !== mese - 1 ||
    prova.getUTCDate() !== giorno
  ) {
    return "";
  }
  return `${anno}-${dueCifre(mese)}-${dueCifre(giorno)}`;
}

/**
 * Un importo della riga: { valore } se buono, { errore } se no.
 * Vuoto → 0. Si accettano numeri e stringhe numeriche (con segno, "$" e
 * separatore delle migliaia tollerati); più di due decimali → errore, come
 * nel form, che li rifiuta in digitazione. Il valore buono è arrotondato a
 * due decimali per spegnere il rumore in virgola mobile delle celle Excel.
 */
export function importoDellaCella(v) {
  if (v === "" || v == null) return { valore: 0 };
  let n;
  if (typeof v === "number") {
    n = v;
  } else {
    const s = normalizzaTesto(v).replace(/[$,\s]/g, "");
    if (!/^[-+]?\d+(\.\d+)?$/.test(s)) return { errore: "invalid amount" };
    n = Number(s);
  }
  if (!isFinite(n)) return { errore: "invalid amount" };
  const arrotondato = Math.round(n * 100) / 100;
  if (Math.abs(arrotondato - n) > 0.000001) return { errore: "more than 2 decimals" };
  return { valore: arrotondato };
}

/**
 * Il numero di riga nel foglio (1 = intestazione), per dire all'utente DOVE
 * guardare. SheetJS annota su ogni oggetto la riga d'origine (`__rowNum__`,
 * base 0), che tiene conto delle righe vuote saltate; se manca, si conta.
 */
function numeroDiRiga(riga, indice) {
  if (typeof riga.__rowNum__ === "number") return riga.__rowNum__ + 1;
  return indice + 2;
}

/**
 * Il vaglio dell'intero foglio.
 *
 * @param {Object[]} righe  le righe del primo foglio, da `sheet_to_json`
 * @param {{contacts: Object[], categories: Object[]}} liste  quelle della PWA
 * @returns {{
 *   errore: string|null,
 *   valide: {riga:number, tx:Object, resa:Object}[],
 *   scartate: {riga:number, motivo:string, ignorata:boolean}[]
 * }}
 * `errore` non nullo = il file non si può leggere (colonne mancanti): nulla
 * entra. `resa` è la riga già interpretata, per l'anteprima. `ignorata` è
 * vera per le righe che si saltano DI PROPOSITO (saldo iniziale, riga
 * vuota): non sono errori.
 */
export function normalizzaFoglio(righe, { contacts = [], categories = [] } = {}) {
  const valide = [];
  const scartate = [];

  if (!Array.isArray(righe) || righe.length === 0) {
    return { errore: "The file has no rows to import.", valide, scartate };
  }

  const intestazioni = new Set();
  for (const r of righe) Object.keys(r).forEach((k) => intestazioni.add(k));
  const mappa = riconosciColonne([...intestazioni]);
  const mancano = colonneMancanti(mappa);
  if (mancano.length) {
    return {
      errore: `The file does not have the expected columns: ${mancano.join(", ")}.`,
      valide,
      scartate,
    };
  }

  const leggi = (r, colonna) => (mappa[colonna] in r ? r[mappa[colonna]] : "");

  // Indici per nome, costruiti una volta: nome → lista dei candidati, così
  // un nome che appartiene a due schede si vede (ed è ambiguo, non "il primo").
  const contattiPerNome = new Map();
  for (const c of contacts) {
    for (const nome of nomiDelContatto(c)) {
      if (!contattiPerNome.has(nome)) contattiPerNome.set(nome, []);
      const lista = contattiPerNome.get(nome);
      if (!lista.includes(c)) lista.push(c);
    }
  }
  const categoriePerNome = new Map();
  for (const cat of categories) {
    const nome = chiaveDiConfronto(cat && cat.name);
    if (!nome) continue;
    if (!categoriePerNome.has(nome)) categoriePerNome.set(nome, []);
    categoriePerNome.get(nome).push(cat);
  }

  righe.forEach((r, indice) => {
    const riga = numeroDiRiga(r, indice);
    const scarta = (motivo, ignorata = false) => scartate.push({ riga, motivo, ignorata });

    const cliente = normalizzaTesto(leggi(r, "client"));
    const categoria = normalizzaTesto(leggi(r, "category"));
    const descrizione = normalizzaTesto(leggi(r, "description"));

    // Il saldo iniziale, per come esce dall'export (Client e Category
    // vuoti) o per come lo si scrive a mano ("INITIAL BALANCE"): si salta.
    const sembraSaldoIniziale =
      descrizione.toUpperCase() === "INITIAL BALANCE" ||
      (!cliente && !categoria && descrizione.toUpperCase().includes("INITIAL BALANCE"));
    if (sembraSaldoIniziale) {
      scarta("initial balance row (ignored on purpose)", true);
      return;
    }

    const importi = {};
    const erroriImporto = [];
    for (const conto of CONTI) {
      const esito = importoDellaCella(leggi(r, conto.colonna));
      if (esito.errore) erroriImporto.push(`${conto.label}: ${esito.errore}`);
      importi[conto.chiave] = esito.errore ? 0 : esito.valore;
    }

    // Una riga senza nulla (né nomi, né descrizione, né importi) è una riga
    // vuota del foglio, non un errore da mostrare.
    const nessunImporto = CONTI.every((c) => importi[c.chiave] === 0);
    if (!cliente && !categoria && !descrizione && nessunImporto && !erroriImporto.length) {
      scarta("empty row (ignored)", true);
      return;
    }

    const date = dataInFormatoDiSistema(leggi(r, "date"));
    if (!date) {
      scarta(`invalid date: '${normalizzaTesto(leggi(r, "date"))}'`);
      return;
    }

    if (!cliente) {
      scarta("client is missing");
      return;
    }
    const candidatiCliente = contattiPerNome.get(chiaveDiConfronto(cliente)) || [];
    if (candidatiCliente.length === 0) {
      scarta(`client not found: '${cliente}'`);
      return;
    }
    if (candidatiCliente.length > 1) {
      scarta(`client name is ambiguous: '${cliente}'`);
      return;
    }

    if (!categoria) {
      scarta("category is missing");
      return;
    }
    const candidatiCategoria = categoriePerNome.get(chiaveDiConfronto(categoria)) || [];
    if (candidatiCategoria.length === 0) {
      scarta(`category not found: '${categoria}'`);
      return;
    }
    if (candidatiCategoria.length > 1) {
      scarta(`category name is ambiguous: '${categoria}'`);
      return;
    }

    if (erroriImporto.length) {
      scarta(erroriImporto.join("; "));
      return;
    }
    const contiMovimentati = CONTI.filter((c) => importi[c.chiave] !== 0);
    if (contiMovimentati.length === 0) {
      scarta("no amount");
      return;
    }
    if (contiMovimentati.length > 1) {
      scarta("more than one amount on the same row");
      return;
    }

    // La forma del form, e nient'altro: stesse sette chiavi di
    // TransactionForm.jsx `handleSubmit`.
    const tx = {
      date,
      clientOrSupplier: candidatiCliente[0],
      description: descrizione,
      category: candidatiCategoria[0],
      bank1: importi.bank1,
      bank2: importi.bank2,
      cash: importi.cash,
    };

    valide.push({
      riga,
      tx,
      resa: {
        date,
        client: nomeDelContatto(candidatiCliente[0]) || cliente,
        description: descrizione,
        category: candidatiCategoria[0].name,
        account: contiMovimentati[0].label,
        amount: importi[contiMovimentati[0].chiave],
      },
    });
  });

  return { errore: null, valide, scartate };
}
